import moment from 'moment';
import environment from '../environments/index';
import {
  MetricResult,
  UserActivityScope,
  UserActivityWindowDays,
} from '../types/adminAnalytics.ts';

export const ACTIVE_USER_METRIC_KEYS = [
  'users_active_last_30d',
  'users_active_last_60d',
  'users_active_last_90d',
  'users_active_last_180d',
  'users_active_last_360d',
] as const;

export type ActiveUserMetricKey = (typeof ACTIVE_USER_METRIC_KEYS)[number];

export const LAST_LOGIN_TRACKING_SINCE =
  environment.analytics?.lastLoginTrackingSince || '2026-03-09';

export function getLastLoginTrackingSinceLabel(): string {
  return moment(LAST_LOGIN_TRACKING_SINCE).format('DD/MM/YYYY');
}

export function getActiveUsersDisclaimer(): string {
  return `Actividad según último login registrado desde el ${getLastLoginTrackingSinceLabel()}. Antes de esa fecha el dato no existía: un last_login vacío no implica que el usuario nunca entró. No refleja cantidad de ingresos.`;
}

export function getLastLoginCompareWarning(compareTo: string | undefined): string | null {
  if (!compareTo) return null;
  if (!moment(compareTo).isBefore(moment(LAST_LOGIN_TRACKING_SINCE).startOf('day'))) {
    return null;
  }
  return `El período de comparación termina antes del ${getLastLoginTrackingSinceLabel()}; los valores "Anterior" y el % pueden ser 0 o poco representativos.`;
}

export function reportHasActiveUserMetrics(
  rows: Array<{ metricKey: string }>
): boolean {
  return rows.some((row) => isActiveUserMetricKey(row.metricKey));
}

export function isActiveUserMetricKey(key: string): key is ActiveUserMetricKey {
  return (ACTIVE_USER_METRIC_KEYS as readonly string[]).includes(key);
}

export function pickActiveUserMetrics(metrics: MetricResult[]): MetricResult[] {
  return ACTIVE_USER_METRIC_KEYS.map((key) => metrics.find((m) => m.key === key)).filter(
    (m): m is MetricResult => m != null
  );
}

export function excludeActiveUserMetrics(metrics: MetricResult[]): MetricResult[] {
  return metrics.filter((m) => !isActiveUserMetricKey(m.key));
}

export function splitUserMetrics(metrics: MetricResult[]): {
  coreMetrics: MetricResult[];
  activeMetrics: MetricResult[];
} {
  return {
    coreMetrics: excludeActiveUserMetrics(metrics),
    activeMetrics: pickActiveUserMetrics(metrics),
  };
}

const METRIC_KEY_TO_WINDOW_DAYS: Record<ActiveUserMetricKey, UserActivityWindowDays> = {
  users_active_last_30d: 30,
  users_active_last_60d: 60,
  users_active_last_90d: 90,
  users_active_last_180d: 180,
  users_active_last_360d: 360,
};

export function windowDaysFromActiveMetricKey(key: string): UserActivityWindowDays | null {
  if (!isActiveUserMetricKey(key)) return null;
  return METRIC_KEY_TO_WINDOW_DAYS[key];
}

export const USER_ACTIVITY_SCOPE_LABELS: Record<UserActivityScope, string> = {
  within: 'Activos en la ventana',
  band: 'Banda exclusiva',
  no_activity: 'Sin last_login',
  inactive_over: 'Inactivos (+N días)',
};
